import { useEffect, useState, useCallback } from "react";
import { Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [doctorId, setDoctorId] = useState("");
  const [minRating, setMinRating] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [page, setPage] = useState(1);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), page_size: "20" });
    if (doctorId) p.set("doctor_id", doctorId);
    if (minRating) p.set("min_rating", minRating);
    const { data } = await api.get(`/admin/reviews?${p.toString()}`);
    setItems(data.items); setTotal(data.total);
  }, [doctorId, minRating, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/doctors?page_size=200").then((r) => setDoctors(r.data.items)); }, []);

  const del = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/admin/reviews/${delTarget}`);
      toast.success("Review deleted; doctor rating recomputed");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout title="Reviews" subtitle={`${total} total`}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={doctorId || "all"} onValueChange={(v) => { setDoctorId(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="admin-reviews-doctor" className="w-56 rounded-full"><SelectValue placeholder="Doctor" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All doctors</SelectItem>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={minRating || "any"} onValueChange={(v) => { setMinRating(v === "any" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="admin-reviews-min" className="w-40 rounded-full"><SelectValue placeholder="Min rating" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any rating</SelectItem>
            {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}★ and up</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div data-testid="admin-reviews-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left p-3">Rating</th><th className="text-left p-3">Patient</th><th className="text-left p-3">Doctor</th>
                <th className="text-left p-3">Comment</th><th className="text-left p-3">Created</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No reviews</td></tr>
                : items.map((r) => (
                <tr key={r.id} data-testid={`admin-review-row-${r.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3"><div className="inline-flex items-center gap-0.5">{[1,2,3,4,5].map((n) => <Star key={n} className={"h-3.5 w-3.5 " + (n <= r.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30")} />)}</div></td>
                  <td className="p-3">{r.patient_name}</td>
                  <td className="p-3">{r.doctor_name}</td>
                  <td className="p-3 text-xs max-w-xs truncate">{r.comment || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" data-testid={`admin-review-delete-${r.id}`} onClick={() => setDelTarget(r.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 20 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <AlertDialogContent data-testid="admin-review-confirm-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review?</AlertDialogTitle>
            <AlertDialogDescription>The doctor's avg rating and total_reviews will be recomputed from remaining reviews.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-review-confirm-delete" onClick={del} className="rounded-full bg-rose-600 hover:bg-rose-600/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
